import React, { useState, useEffect } from 'react';
import './GroupView.css';
import AboutTab from './AboutTab';
import MembersTab from './MembersTab';
import MediaTab from './MediaTab';
import FilesTab from './FilesTab';
import supabase from "../../../supabaseClient";
import { useAuth } from '../../../contexts/authContext';
import { useNavigate } from 'react-router-dom';
import { FaEdit } from 'react-icons/fa';
import ForumTab from './ForumTab';

const GroupView = ({ groupId, posts, groupMembers, postId, onBack }) => {
  const { user: currentUser } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('about');
  const [group, setGroup] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isMember, setIsMember] = useState(false);
  const [showGroupMenu, setShowGroupMenu] = useState(false);
  const [bannerUploading, setBannerUploading] = useState(false);
  const [bannerFile, setBannerFile] = useState(null);
  const [bannerPreview, setBannerPreview] = useState(null);
  const [showUploadControls, setShowUploadControls] = useState(false);

  useEffect(() => {
    if (!groupId) return;
    const fetchGroup = async () => {
      const { data, error } = await supabase
        .from('groups')
        .select('*')
        .eq('id', groupId)
        .single();
      if (error) return;
      setGroup(data);
      let admin = false;
      let member = false;
      if (currentUser && data.created_by === currentUser.id) {
        admin = true;
        member = true;
      } else if (currentUser) {
        // Check group_members table for membership and admin role
        const { data: memberData, error: memberError } = await supabase
          .from('group_members')
          .select('role')
          .eq('group_id', groupId)
          .eq('user_id', currentUser.id)
          .single();
        if (!memberError && memberData) {
          member = true;
          if (memberData.role === 'Admin') {
            admin = true;
          }
        }
      }
      setIsAdmin(admin);
      setIsMember(member);
    };
    fetchGroup();
  }, [groupId, currentUser]);

  // Non-members should not be able to navigate away from About
  useEffect(() => {
    if (!isMember && activeTab !== 'about') {
      setActiveTab('about');
    }
  }, [isMember, activeTab]);

  const handleBannerFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setBannerFile(file);
    setBannerPreview(URL.createObjectURL(file));
  };

  const handleBannerUpload = async () => {
    if (!bannerFile || !group) return;
    setBannerUploading(true);
    try {
      const fileExt = bannerFile.name.split('.').pop();
      const fileName = `banner_${group.id}_${Date.now()}.${fileExt}`;
      const filePath = `group-banners/${fileName}`;
      // Upload to Supabase Storage
      const { error: uploadError } = await supabase.storage
        .from('banners')
        .upload(filePath, bannerFile, { upsert: true });
      if (uploadError) throw uploadError;
      // Get public URL
      const { data } = supabase.storage.from('banners').getPublicUrl(filePath);
      const publicUrl = data?.publicUrl;
      if (!publicUrl) throw new Error('Failed to get public URL');
      // Update group record
      const { error: updateError } = await supabase
        .from('groups')
        .update({ group_banner_url: publicUrl })
        .eq('id', group.id);
      if (updateError) throw updateError;
      // Refresh group data
      setGroup((prev) => ({ ...prev, group_banner_url: publicUrl }));
      setBannerFile(null);
      setBannerPreview(null);
    } catch (err) {
      alert('Failed to upload banner.');
    } finally {
      setBannerUploading(false);
    }
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'about':
        return <AboutTab groupId={groupId} isMember={isMember} onJoinGroupSuccess={() => setIsMember(true)} />;
      case 'forum':
        return (
          <div className="group-forum">
            <ForumTab groupId={groupId} groupMembers={groupMembers} userProfile={currentUser} supabase={supabase} postId={postId} />
          </div>
        );
      case 'members':
        return (
          <div className="group-members">
            <MembersTab groupId={groupId} />
          </div>
        );
      case 'media':
        return (
          <div className="group-media">
            <MediaTab groupId={groupId} />
          </div>
        );
      case 'files':
        return (
          <div className="group-files">
            <FilesTab group={group} />
          </div>
        );
      default:
        return null;
    }
  };

  if (!group) {
    return <div className="group-view">Loading group...</div>;
  }

  return (
    <div className="group-view">
      {onBack && (
        <div className="group-view-back">
          <button type="button" className="group-back-btn" onClick={onBack}>
            &larr; Back to groups
          </button>
        </div>
      )}
      <div className="group-header">
        {isAdmin && (
          <div className="gdm-kebab-wrapper group-header-kebab-wrapper">
            <button
              className="gdm-kebab-btn"
              type="button"
              onClick={() => setShowGroupMenu(prev => !prev)}
              aria-label="More options"
            >
              ⋮
            </button>
            {showGroupMenu && (
              <div className="gdm-kebab-menu">
                <button
                  type="button"
                  className="gdm-menu-item"
                  onClick={() => {
                    navigate('/app/groups');
                  }}
                >
                  Edit Group
                </button>
                <div className="gdm-menu-danger-label">Danger zone</div>
                <button
                  type="button"
                  className="gdm-menu-item danger"
                  onClick={async () => {
                    const confirmed = window.confirm('Are you sure you want to delete this group? This action cannot be undone.');
                    if (!confirmed) return;
                    const { error } = await supabase
                      .from('groups')
                      .delete()
                      .eq('id', group.id);
                    if (error) {
                      alert('Failed to delete group');
                      return;
                    }
                    navigate('/app/groups');
                  }}
                >
                  Delete Group
                </button>
              </div>
            )}
          </div>
        )}
        {/* Admin badge next to kebab */}
        {isAdmin && <span className="group-header-admin-badge">Admin</span>}
        {/* Banner at the top spanning full width */}
        <div className="group-banner">
          {group.group_banner_url ? (
            <img src={group.group_banner_url} alt={group.name} className="group-banner-img" />
          ) : (
            <div className="banner-placeholder"></div>
          )}
          {/* Admin edit icon - always visible for admins */}
          {isAdmin && (
            <>
              <button 
                className="banner-edit-icon" 
                onClick={() => setShowUploadControls(!showUploadControls)}
                title="Change banner image"
              >
                <FaEdit className="banner-edit-icon-icon" />
              </button>
              
              {/* Banner upload controls - only visible when edit icon is clicked */}
              {showUploadControls && (
                <div className="banner-upload-controls">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleBannerFileChange}
                    style={{ display: 'inline-block', marginRight: 8 }}
                    disabled={bannerUploading}
                  />
                  {bannerPreview && (
                    <img src={bannerPreview} alt="Preview" style={{ maxHeight: 60, marginRight: 8, borderRadius: 4 }} />
                  )}
                  <button
                    onClick={handleBannerUpload}
                    disabled={!bannerFile || bannerUploading}
                    style={{ marginRight: 8 }}
                  >
                    {bannerUploading ? 'Uploading...' : group.group_banner_url ? 'Change Banner' : 'Upload Banner'}
                  </button>
                  {bannerFile && !bannerUploading && (
                    <button onClick={() => { setBannerFile(null); setBannerPreview(null); }}>Cancel</button>
                  )}
                  <button 
                    onClick={() => setShowUploadControls(false)} 
                    className="close-controls-btn"
                  >
                    Close
                  </button>
                </div>
              )}
            </>
          )}
          <div className="group-title-area">
            <h2>{group.name}</h2>
          </div>
          <div className="group-actions">
            <button
              className="join-room-btn"
              type="button"
              disabled={!group.room_id}
              onClick={() => {
                if (group.room_id) {
                  navigate(`/app/virtualcafe/${group.room_id}`);
                }
              }}
            >
              Join Virtual Room
            </button>
            <button className="invite-btn">Invite</button>
            <button className="share-btn">Share</button>
          </div>
        </div>
        {/* Content below the banner */}
        <div className="group-header-content">
        </div>
      </div>
      <div className="group-tabs">
        <button
          className={`tab-btn ${activeTab === 'about' ? 'active' : ''}`}
          onClick={() => setActiveTab('about')}
        >
          About
        </button>
        {isMember && (
          <>
            <button
              className={`tab-btn ${activeTab === 'forum' ? 'active' : ''}`}
              onClick={() => setActiveTab('forum')}
            >
              Forum
            </button>
            <button
              className={`tab-btn ${activeTab === 'members' ? 'active' : ''}`}
              onClick={() => setActiveTab('members')}
            >
              Members
            </button>
            <button
              className={`tab-btn ${activeTab === 'media' ? 'active' : ''}`}
              onClick={() => setActiveTab('media')}
            >
              Media
            </button>
            <button
              className={`tab-btn ${activeTab === 'files' ? 'active' : ''}`}
              onClick={() => setActiveTab('files')}
            >
              Files
            </button>
          </>
        )}
      </div>
      <div className="tab-content">
        {renderTabContent()}
      </div>
    </div>
  );
};

export default GroupView;